<template>
  <div class="change-password-container">
    <div class="change-password-card">
      <h2>Change Password</h2>
      
      <form @submit.prevent="changePassword">
        <div class="form-group">
          <label for="currentPassword">Current Password</label>
          <Password 
            id="currentPassword"
            v-model="form.currentPassword"
            :feedback="false"
            toggleMask
            :class="{ 'p-invalid': v$.currentPassword.$error }"
            placeholder="Enter current password"
          />
          <small v-if="v$.currentPassword.$error" class="p-error">
            {{ v$.currentPassword.$errors[0].$message }}
          </small>
        </div>

        <div class="form-group">
          <label for="newPassword">New Password</label>
          <Password 
            id="newPassword"
            v-model="form.newPassword"
            :feedback="true"
            toggleMask
            :class="{ 'p-invalid': v$.newPassword.$error }"
            placeholder="Enter new password"
            promptLabel="Choose a password"
            weakLabel="Too simple"
            mediumLabel="Average complexity"
            strongLabel="Complex password"
          />
          <small v-if="v$.newPassword.$error" class="p-error">
            {{ v$.newPassword.$errors[0].$message }}
          </small>
          <div class="password-requirements">
            <p>Password must contain:</p>
            <ul>
              <li :class="{ valid: hasMinLength }">At least 8 characters</li>
              <li :class="{ valid: hasUppercase }">One uppercase letter</li>
              <li :class="{ valid: hasLowercase }">One lowercase letter</li>
              <li :class="{ valid: hasNumber }">One number</li>
              <li :class="{ valid: hasSpecialChar }">One special character</li>
            </ul>
          </div>
        </div>

        <div class="form-group">
          <label for="confirmPassword">Confirm New Password</label>
          <Password 
            id="confirmPassword"
            v-model="form.confirmPassword"
            :feedback="false"
            toggleMask
            :class="{ 'p-invalid': v$.confirmPassword.$error }"
            placeholder="Confirm new password"
          />
          <small v-if="v$.confirmPassword.$error" class="p-error">
            {{ v$.confirmPassword.$errors[0].$message }}
          </small>
        </div>

        <div class="form-actions">
          <Button 
            type="submit" 
            label="Change Password" 
            :loading="loading"
            :disabled="v$.$invalid"
            class="p-button-primary"
          />
          <Button 
            type="button" 
            label="Cancel" 
            class="p-button-secondary"
            @click="$router.go(-1)"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, sameAs, helpers } from '@vuelidate/validators'
import Password from 'primevue/password'
import Button from 'primevue/button'
import axios from '../axios'
import Swal from 'sweetalert2'

const router = useRouter()
const loading = ref(false)

const form = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

// Password complexity validation
const passwordComplexity = helpers.withMessage(
  'Password must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character',
  (value) => {
    if (!value) return false
    const hasMinLength = value.length >= 8
    const hasUppercase = /[A-Z]/.test(value)
    const hasLowercase = /[a-z]/.test(value)
    const hasNumber = /\d/.test(value)
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value)
    return hasMinLength && hasUppercase && hasLowercase && hasNumber && hasSpecialChar
  }
)

const rules = {
  currentPassword: { required },
  newPassword: { 
    required, 
    minLength: minLength(8),
    passwordComplexity
  },
  confirmPassword: { 
    required, 
    sameAs: sameAs(computed(() => form.value.newPassword))
  }
}

const v$ = useVuelidate(rules, form)

// Password strength indicators
const hasMinLength = computed(() => form.value.newPassword.length >= 8)
const hasUppercase = computed(() => /[A-Z]/.test(form.value.newPassword))
const hasLowercase = computed(() => /[a-z]/.test(form.value.newPassword))
const hasNumber = computed(() => /\d/.test(form.value.newPassword))
const hasSpecialChar = computed(() => /[!@#$%^&*(),.?":{}|<>]/.test(form.value.newPassword))

const changePassword = async () => {
  const isFormCorrect = await v$.value.$validate()
  if (!isFormCorrect) return

  loading.value = true
  
  try {
    await axios.post('/change-password', {
      current_password: form.value.currentPassword,
      new_password: form.value.newPassword,
      new_password_confirmation: form.value.confirmPassword
    })

    await Swal.fire({
      title: 'Success!',
      text: 'Password changed successfully',
      icon: 'success',
      confirmButtonText: 'OK'
    })

    router.push('/')
  } catch (error) {
    Swal.fire({
      title: 'Error!',
      text: error.response?.data?.message || 'Failed to change password',
      icon: 'error',
      confirmButtonText: 'OK'
    })
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.change-password-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  padding: 20px;
}

.change-password-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  width: 100%;
  max-width: 500px;
}

.change-password-card h2 {
  text-align: center;
  margin-bottom: 2rem;
  color: #333;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

.form-group :deep(.p-password) {
  width: 100%;
}

.form-group :deep(.p-password input) {
  width: 100%;
}

.password-requirements {
  margin-top: 0.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.password-requirements p {
  margin: 0 0 0.5rem 0;
  font-weight: 600;
  color: #495057;
}

.password-requirements ul {
  margin: 0;
  padding-left: 1.5rem;
}

.password-requirements li {
  color: #dc3545;
  margin-bottom: 0.25rem;
}

.password-requirements li.valid {
  color: #28a745;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
}

.p-error {
  color: #dc3545;
  font-size: 0.875rem;
}
</style>